import React, { Fragment, useLayoutEffect, useRef, useState } from 'react'
import {
	getQuestionOptionIndex,
	Image,
	isMultipleSelect,
	isMultipleChoice,
} from '../../../utils'
import {
	MultipleSelect as MultipleSelectType,
	MultipleChoice as MultipleChoiceType,
	ClientFields,
	QuestionChoice,
} from '@mission.io/question-toolkit'
import classnames from 'classnames'
import { QuestionSlotProps } from '../../QuestionSlot'
import { ChoiceWrapper } from '../../../assets/styles'
import 'styled-components/macro'
import { styled } from 'styled-components'
import OptionalMarkdown from '../../../components/Markdown/OptionalMarkdown'
import { _tempTheme } from '../../../utils/tempTheme'

interface MultipleSelectProps extends QuestionSlotProps {
	question: MultipleSelectType<ClientFields> | MultipleChoiceType<ClientFields>
}
const SMALL_HEIGHT_CHOICE_WRAPPER = 310
/**
 * A component for a Multiple Select Question Slot view.
 * @param {x: MultipleSelectProps} props
 * @returns {JSX.Element}
 */
export const MultipleSelect = (props: MultipleSelectProps): JSX.Element => {
	const { question } = props
	const choiceWrapperRef = useRef<HTMLDivElement | null>(null)
	const [heightWrapper, setHeightWrapper] = useState(0)
	if (
		!question ||
		!(isMultipleSelect(question) || isMultipleChoice(question))
	) {
		return <Fragment />
	}
	const choicesHaveImage = Boolean(
		question?.choices.some((choice: QuestionChoice) => !!choice.media?.[0])
	)
	const choicesHaveText = Boolean(
		question?.choices.some((choice: QuestionChoice) => !!choice.text)
	)
	const allChoicesHaveOnlyText = question?.choices.every(
		(choice: QuestionChoice) => !choice.media || choice.media.length === 0
	)
	const hasQuestionImage = question.media && question.media?.length > 0
	useLayoutEffect(() => {
		if (choiceWrapperRef.current)
			setHeightWrapper(choiceWrapperRef.current.clientHeight)
	}, [choiceWrapperRef.current])

	const isSmall =
		heightWrapper !== 0 && heightWrapper < SMALL_HEIGHT_CHOICE_WRAPPER

	const [imageAspectRatio, setImageAspectRatio] = useState(0)
	const isImageLandscape = imageAspectRatio > 1
	const imageSrc =
		// @ts-expect-error TS2532 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
		question.media && question.media.length > 0 ? question.media[0].url : null
	const isPortraitAndOnlyText =
		!isImageLandscape && Boolean(imageSrc) && allChoicesHaveOnlyText

	if (question)
		return (
			<div
				style={
					isImageLandscape
						? { gridTemplateRows: '1fr auto' }
						: { gridTemplateColumns: 'auto 1fr' }
				}
				className={`grid gap-4 py-4 h-full ${
					isPortraitAndOnlyText ? 'w-[75%] ml-auto mr-auto' : ''
				}`}>
				<div
					className="relative min-h-72"
					style={
						!isImageLandscape ? { aspectRatio: `${imageAspectRatio} / 1` } : {}
					}>
					{imageSrc ? (
						<img
							className={classnames(
								'absolute max-w-full max-h-full',
								isImageLandscape
									? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
									: ''
							)}
							onLoad={event => {
								if (!event?.target) return
								const image = event.target as HTMLImageElement
								setImageAspectRatio(
									Math.ceil(100 * (image.naturalWidth / image.naturalHeight)) /
										100
								)
							}}
							src={imageSrc}
							alt={question.phrase}
						/>
					) : null}
				</div>
				<ChoiceWrapperStyled
					className="!gap-4"
					ref={choiceWrapperRef}
					$withImage={choicesHaveImage}
					$choicesHaveText={choicesHaveText}
					$hasQuestionImage={hasQuestionImage}
					$isPortraitAndOnlyText={isPortraitAndOnlyText}>
					{question.choices.map((choice: QuestionChoice, index: number) => {
						const hasMedia = Boolean(choice.media && choice.media.length > 0)
						const hasText = Boolean(choice.text)
						return (
							<ChoiceBox
								key={String(choice._id)}
								$isSmall={isSmall}
								$isImageLandscape={isImageLandscape}
								$hasQuestionImage={hasQuestionImage}
								$choicesHaveImage={choicesHaveImage}
								$hasText={hasText}>
								{hasMedia && (
									<div className="mx-auto">
										<Image
											className="min-w-24"
											media={choice.media}
											css={`
												margin: auto;
												height: 100%;

												object-fit: contain;
												${isSmall ? 'width: auto;' : ''}
												${isSmall && choicesHaveImage && choicesHaveText
													? 'margin-right: 0px;'
													: ''}
											`}
										/>
									</div>
								)}

								{(hasText || !hasMedia) && (
									<ChoiceText $hasMedia={hasMedia}>
										{hasText ? (
											<OptionalMarkdown>{choice.text}</OptionalMarkdown>
										) : (
											<span>No Image</span>
										)}
									</ChoiceText>
								)}
								<Index>{getQuestionOptionIndex(index)}</Index>
							</ChoiceBox>
						)
					})}
				</ChoiceWrapperStyled>
			</div>
		)

	return <Fragment />
}

const Index = styled.div`
	background-color: ${({ theme }) => theme.lightSecondaryColor};
	color: ${({ theme }) => theme.white};
	padding: ${_tempTheme.spacing2};
	font-family: ${({ theme }) => theme.headerFontFamily};
	font-size: 2em;
	min-height: 1.8em;
	display: flex;
	justify-content: center;
	align-items: center;
`
const ChoiceText = styled.div<{ $hasMedia?: boolean }>`
	text-align: center;
	font-size: ${_tempTheme.text_3xl};
	padding: ${_tempTheme.spacing2};
	flex: ${({ $hasMedia: hasMedia }) => (!hasMedia ? '1;' : 'initial;')}
	display: flex;
	flex-direction: column;
	justify-content: center;
	span {
		align-self: center;
	}
`

const ChoiceBox = styled.div<{
	$hasText?: boolean
	$isSmall?: boolean
	$hasQuestionImage?: boolean
	$isImageLandscape?: boolean
	$choicesHaveImage?: boolean
}>`
	overflow: hidden;
	display: flex;
	height: 100%;
	max-height: 100%;
	width: 100%;
	cursor: default;

	flex-direction: ${({
		$choicesHaveImage: choicesHaveImage,
		$hasQuestionImage: hasQuestionImage,
		$isSmall: isSmall,
		$isImageLandscape: isImageLandscape,
	}) =>
		(isSmall && hasQuestionImage) || choicesHaveImage || !isImageLandscape
			? `row-reverse`
			: `column`};
	${({ theme }) => `
		border-radius: ${_tempTheme.spacing1};
		background-color: ${theme.lighterSecondaryColor};
		color: ${theme.lightSecondaryColor};
	`}

	.image-wrapper {
		overflow: hidden;
		width: 100%;
		height: 100%;
		margin: auto;
	}
`

const ChoiceWrapperStyled = styled(ChoiceWrapper)<{
	$hasQuestionImage?: boolean
	$choicesHaveText?: boolean
	$withImage?: boolean
	$isPortraitAndOnlyText?: boolean
}>`
	margin: 0 auto;
	height: 100%;
	overflow: hidden;
	width: 100%;

	${({ $withImage, $hasQuestionImage, $isPortraitAndOnlyText }) => `
		height: ${$withImage ? '100%' : 'fit-content'};
		flex: ${$withImage && $hasQuestionImage ? '1' : 'initial'};
		grid-template-columns: ${$isPortraitAndOnlyText ? '1fr' : '1fr 1fr'};
	
	`}
`

export default MultipleSelect
